/**
 * 生图详情弹窗（Alice 的 #modal-image-detail）：原图预览、请求元信息、提示词、复制与下载。
 *
 * 背景：生图记录的列表接口已带齐元信息，所以这里不再请求详情；原图来自 /control/admin/image-proxy，
 *   后端只回传「正文仍在保存期内」的响应里的图片，并校验文件头，所以很多记录没有预览。
 * 设计意图：没有预览或提示词时说明原因（正文已过保存期被清理，或产生在统一保存正文之前），不留空白；成本按整单均分到单张，
 *   和画廊卡片口径一致；导出 JSON 是把当前记录存成文件，不再请求后端。
 * 关键约束：预览图 onError 要回落到占位提示（过期或被清理）；复制失败（非 https、无权限）要给出提示。
 */
import { useEffect, useState } from 'react'
import type { ImageRecord } from '@ap/api-client/admin-runtime'
import { imagePreviewUrl, saveBlob } from '@ap/api-client/admin-runtime'
import { AG_runtime as G } from '@ap/i18n/admin-runtime'
import { useI18n } from '@ap/i18n'
import { fmtDateTime, useToast } from '@ap/ui'
import { AliceModal, fmtDuration, useMoney } from '../../../alice/kit-runtime.tsx'

const heading = { fontSize: 11, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 6 } as const
const box = {
  background: 'var(--bg-subtle)',
  border: '1px solid var(--border-medium)',
  borderRadius: 6,
  padding: 12,
  fontSize: 12,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  maxHeight: 180,
  overflow: 'auto',
  margin: 0,
} as const

export function ImageDetailModal({ record, onClose }: { record: ImageRecord | null; onClose: () => void }) {
  const { t, locale } = useI18n()
  const toast = useToast()
  const money = useMoney()
  const [index, setIndex] = useState(0)
  const [broken, setBroken] = useState(false)
  // 关闭动画期间 record 已为 null，保留最后一条避免内容闪空
  const [last, setLast] = useState<ImageRecord | null>(null)
  useEffect(() => {
    if (record) {
      setLast(record)
      setIndex(0)
      setBroken(false)
    }
  }, [record])
  const r = record ?? last

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.ok(t(G.common.copied))
    } catch (e) {
      console.error('[images] 复制失败', e)
      toast.error(String(e))
    }
  }

  if (!r) return <AliceModal open={false} onClose={onClose} title="" width={760}>{null}</AliceModal>
  const failed = r.status === 'failed'
  const dash = '—'
  const url = imagePreviewUrl(r.request_id, index)
  const hasPreview = !failed && r.preview_count > 0 && !broken
  // 单张上游成本由整单上游成本按图片数均分；成本为空表示渠道没有采购价，不当 0
  const perImage = r.upstream_cost === null || r.images <= 0 ? null : Math.floor(r.upstream_cost / r.images)
  const fields: [string, string][] = [
    [t(G.images.dAccountName), r.account_name || dash],
    [t(G.images.dAccountId), r.account_id],
    [t(G.images.dEndUser), r.end_user || dash],
    [t(G.images.dModel), r.shell_display_name ? `${r.shell_display_name} (${r.model})` : r.model],
    [t(G.images.dChannel), r.channel_id || dash],
    [t(G.images.dUpstreamModel), r.upstream_model || dash],
    [t(G.images.dCostPer), failed ? dash : perImage === null ? t(G.images.dCostUnknown) : money.usd(perImage, 6)],
    [t(G.images.dCostTotal), r.upstream_cost === null ? dash : `${money.usd(r.upstream_cost, 6)} (${t(G.images.dCostNote, { n: Math.max(1, r.images), basis: r.upstream_cost_basis || t(G.images.dBasisNone) })})`],
    [t(G.images.dCharge), `${money.petals(r.cost)} (${money.usd(r.cost, 6)})`],
    [t(G.images.dTime), fmtDateTime(r.created_at, locale) || dash],
    [t(G.images.dDuration), r.duration_ms === null ? dash : fmtDuration(r.duration_ms)],
    [t(G.images.dQueueWait), fmtDuration(r.queue_wait_ms)],
    [t(G.images.dRequestId), r.request_id],
    [t(G.images.dStatusCode), String(r.status_code)],
    [t(G.images.dIp), r.client_ip || dash],
    [t(G.images.dDevice), r.device_id || dash],
    [t(G.images.dSource), r.source || dash],
    [t(G.images.dVersion), r.app_version || dash],
  ]

  return (
    <AliceModal open={record !== null} onClose={onClose} title={failed ? t(G.images.dTitleFailed) : t(G.images.dTitle)} width={760} dismissOnMask>
      <div style={{ marginBottom: 16, textAlign: 'center' }}>
        {hasPreview ? (
          <>
            <img
              key={url}
              src={url}
              alt=""
              style={{ maxWidth: '100%', maxHeight: 420, borderRadius: 8, background: 'var(--bg-muted)' }}
              onError={() => {
                console.warn('[images] 预览加载失败', r.request_id, index)
                setBroken(true)
              }}
            />
            {r.preview_count > 1 && (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center', marginTop: 8, fontSize: 12 }}>
                <button type="button" className="btn btn-secondary btn-sm" disabled={index <= 0} onClick={() => setIndex(index - 1)}>
                  {t(G.common.prev)}
                </button>
                <span>{t(G.images.dPreviewNav, { i: index + 1, n: r.preview_count })}</span>
                <button type="button" className="btn btn-secondary btn-sm" disabled={index >= r.preview_count - 1} onClick={() => setIndex(index + 1)}>
                  {t(G.common.next)}
                </button>
              </div>
            )}
          </>
        ) : (
          <div style={{ padding: 24, borderRadius: 8, background: 'var(--bg-muted)', color: 'var(--text-tertiary)', fontSize: 12 }}>
            {failed ? t(G.images.failedShort) : broken ? t(G.images.previewFailed) : t(G.images.dPreviewNone)}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', border: '1px solid var(--border-medium)', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
        {fields.map(([k, v], i) => (
          <div key={k} style={{ padding: '8px 14px', borderBottom: '1px solid var(--border)', ...(i % 2 === 0 ? { borderRight: '1px solid var(--border)', background: 'var(--bg-subtle)' } : {}) }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 2 }}>{k}</div>
            <div style={{ fontSize: 13, fontFamily: 'var(--mono)', wordBreak: 'break-all' }}>{v}</div>
          </div>
        ))}
      </div>

      {failed && r.error_message && (
        <div style={{ marginBottom: 12 }}>
          <div style={heading}>{t(G.images.dError)}</div>
          <pre style={{ ...box, color: 'var(--error-text)' }}>{r.error_message}</pre>
        </div>
      )}
      <div style={{ marginBottom: 12 }}>
        <div style={heading}>{t(G.images.dPrompt)}</div>
        <pre style={box}>{r.prompt || t(G.images.dPromptNone)}</pre>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {failed && r.error_message && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => void copy(r.error_message)}>
            {t(G.images.dCopyError)}
          </button>
        )}
        {r.prompt && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => void copy(r.prompt)}>
            {t(G.images.dCopyPrompt)}
          </button>
        )}
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => void copy(r.request_id)}>
          {t(G.images.dCopyRequest)}
        </button>
        {hasPreview && (
          <>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => void copy(new URL(url, window.location.origin).href)}>
              {t(G.images.dCopyLink)}
            </button>
            <a className="btn btn-secondary btn-sm" href={url} download={`${r.request_id}-${index + 1}`}>
              {t(G.images.dDownload)}
            </a>
          </>
        )}
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => saveBlob(new Blob([`${JSON.stringify(r, null, 2)}\n`], { type: 'application/json' }), `image-log-${r.request_id}.json`)}
        >
          {t(G.common.exportJson)}
        </button>
      </div>
    </AliceModal>
  )
}
